import React, { useState } from 'react'
import styled from 'styled-components'
import { useBaseInfo } from 'apis/hooks'
import { Chip, ChipSize, ChipWrapper } from 'components/common/chip'
import { Column, Row } from 'components/common/layout'
import { PositionType } from 'types/common'
import { observer } from 'mobx-react'
import { ResetBtn } from 'components/misc/selector/reset-button'
import { getParentPositionTypes } from 'utils/formatters'

export const PositionSelector: React.FC<{
  selectedPositionTypes: PositionType[]
  onChange: (p: PositionType[]) => void
  isFilter?: boolean
  maxLength?: number
  chipSize?: ChipSize
}> = observer(
  ({ selectedPositionTypes, onChange, isFilter, maxLength, chipSize }) => {
    const { data: baseInfo } = useBaseInfo()
    // 선택된 대분류 id
    const [selectedParentPositionTypeId, setSelectedParentPositionTypeId] =
      useState<number | null>(
        selectedPositionTypes.length > 0
          ? selectedPositionTypes[0].parentId
          : null,
      )
    const selectedPositionIds = selectedPositionTypes.map((item) => item.id)
    // 선택된 대분류의 포지션
    const positionOfSelectedParentPosition =
      (selectedParentPositionTypeId &&
        baseInfo?.positionTypes.filter(
          (item) =>
            selectedParentPositionTypeId === item.parentId &&
            !item.isDeprecated,
        )) ||
      []
    const parentPositionTypes = getParentPositionTypes(
      baseInfo?.positionTypes || [],
    )

    const isSelectedAllPosition =
      positionOfSelectedParentPosition.filter(
        (item) =>
          item.parentId === selectedParentPositionTypeId && !item.isDeprecated,
      ).length ===
      selectedPositionTypes.filter(
        (item) => item.parentId === selectedParentPositionTypeId,
      ).length

    // 전체 선택
    const selectAllPosition = () => {
      let result = selectedPositionTypes
      result = result.filter(
        (item) => item.parentId !== selectedParentPositionTypeId,
      )
      result = [
        ...result,
        ...positionOfSelectedParentPosition.filter(
          (item) => item.parentId === selectedParentPositionTypeId,
        ),
      ]
      onChange(result)
    }

    // 대분류 선택
    const toggleParentPositionType = (item: PositionType) => {
      setSelectedParentPositionTypeId(item.id)
      if (isFilter) {
        if (item.id === selectedParentPositionTypeId) {
          setSelectedParentPositionTypeId(null)
          onChange([])
        } else {
          onChange(
            baseInfo?.positionTypes.filter(
              (_item) => _item.parentId === item.id && !_item.isDeprecated,
            ) || [],
          )
        }
      }
    }

    // 포지션 선택
    const togglePositionType = (item: PositionType) => {
      let result = selectedPositionTypes
      if (isFilter && isSelectedAllPosition) {
        // 전체선택 된 상태에서 포지션 클릭 시
        result = [
          ...result.filter(
            (item) => item.parentId !== selectedParentPositionTypeId,
          ),
          item,
        ]
      } else {
        // 토글 포지션
        if (selectedPositionIds.includes(item.id)) {
          result = result.filter((_item) => _item.id !== item.id)
        } else {
          if (maxLength && maxLength <= result.length)
            return alert(`최대 ${maxLength}개까지 선택 가능합니다.`)
          result = [...result, item]
        }
        if (isFilter && result.length === 0) {
          result = result.filter(
            (item) => item.parentId !== selectedParentPositionTypeId,
          )
          result = [
            ...result,
            ...positionOfSelectedParentPosition.filter(
              (item) => item.parentId === selectedParentPositionTypeId,
            ),
          ]
        }
      }
      onChange(result)
    }

    return (
      <>
        {isFilter && (
          <Row style={{ justifyContent: 'flex-end', paddingBottom: 16 }}>
            <ResetBtn
              onClick={() => {
                onChange([])
                setSelectedParentPositionTypeId(null)
              }}
            />
          </Row>
        )}
        <PositionContainer>
          <ParentContainer>
            {parentPositionTypes.map((item) => (
              <Chip
                key={item.id}
                size={chipSize}
                isSelected={selectedParentPositionTypeId === item.id}
                onClick={() => toggleParentPositionType(item)}
              >
                {item.name}
              </Chip>
            ))}
          </ParentContainer>
          {positionOfSelectedParentPosition.length > 0 && (
            <ChildrenContainer>
              <ChildrenWrapper>
                {isFilter && (
                  <Chip
                    isSelected={
                      positionOfSelectedParentPosition.filter(
                        (item) =>
                          item.parentId === selectedParentPositionTypeId,
                      ).length ===
                      selectedPositionTypes.filter(
                        (item) =>
                          item.parentId === selectedParentPositionTypeId,
                      ).length
                    }
                    size={chipSize}
                    onClick={selectAllPosition}
                  >
                    전체
                  </Chip>
                )}
                {positionOfSelectedParentPosition.map((item) => (
                  <Chip
                    key={item.id}
                    isSelected={
                      isFilter
                        ? positionOfSelectedParentPosition.filter(
                            (item) =>
                              item.parentId === selectedParentPositionTypeId,
                          ).length !==
                            selectedPositionTypes.filter(
                              (item) =>
                                item.parentId === selectedParentPositionTypeId,
                            ).length && selectedPositionIds.includes(item.id)
                        : selectedPositionIds.includes(item.id)
                    }
                    size={chipSize}
                    onClick={() => togglePositionType(item)}
                  >
                    {item.name}
                  </Chip>
                ))}
              </ChildrenWrapper>
            </ChildrenContainer>
          )}
        </PositionContainer>
      </>
    )
  },
)

const PositionContainer = styled(Column)`
  gap: 16px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    flex: 1;
    overflow: hidden;
    flex-direction: row;
    justify-content: start;
  }
`

const ParentContainer = styled(ChipWrapper)`
  gap: 8px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    align-items: start;
    flex-direction: column;
  }
`

const ChildrenWrapper = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
  @media ${(p) => p.theme.deviceSize.mobile} {
    height: fit-content;
  }
`

const ChildrenContainer = styled(Row)`
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding-top: 16px;
    border-top: 1px solid ${(p) => p.theme.color.line};
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    flex: 1;
    padding-left: 16px;
    padding-bottom: 16px;
    align-items: start;
    overflow: hidden scroll;
    border-left: 1px solid ${(p) => p.theme.color.line};
  }
`
